import { InterceptionMatch, ShellInterceptions } from "@mf-tools/cli-common";
import { logger } from "@mf-tools/cli-common";

const log = logger.browser('path-matcher');

/**
 * Simple LRU (Least Recently Used) cache implementation
 * Used to cache path matching results for performance
 */
class LRUCache<K, V> {
  private cache = new Map<K, V>();
  private maxSize: number;

  constructor(maxSize: number = 100) {
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key: K, value: V): void {
    // Remove if exists (to re-add at end)
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // Add to end
    this.cache.set(key, value);

    // Evict oldest if over capacity
    if (this.cache.size > this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }
}

/**
 * Compiled pattern with regex and parameter names
 */
interface CompiledPattern {
  regex: RegExp;
  paramNames: string[];
}

/**
 * Result of matching a path against a pattern
 */
interface MatchResult {
  matched: boolean;
  params: Record<string, string>;
}

/**
 * PathMatcher handles matching incoming paths against parameterized route patterns
 *
 * Supports patterns like:
 * - /frameworks/:frameworkId/overview
 * - /frameworks/folders/:folderId
 * - /admin/*
 *
 * Features:
 * - Pattern compilation caching
 * - Match result caching (LRU)
 * - Query string stripping
 * - Parameter extraction
 */
export class PathMatcher {
  private patternCache = new Map<string, CompiledPattern>();
  private matchCache = new LRUCache<string, InterceptionMatch | null>(100);
  private interceptions: ShellInterceptions;

  constructor(interceptions: ShellInterceptions) {
    this.interceptions = interceptions;
  }

  /**
   * Compile a route pattern into a regular expression
   *
   * @example
   * "/frameworks/:id" -> { regex: /^\/frameworks\/([^/]+)$/, paramNames: ["id"] }
   * "/admin/*" -> { regex: /^\/admin\/.*$/, paramNames: [] }
   */
  private compilePattern(pattern: string): CompiledPattern {
    // Check cache first
    if (this.patternCache.has(pattern)) {
      return this.patternCache.get(pattern)!;
    }

    const paramNames: string[] = [];

    // Escape special regex characters except : and *
    let regexPattern = pattern
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&');

    // Replace :paramName with capture groups
    regexPattern = regexPattern.replace(/:([^/]+)/g, (match, paramName) => {
      paramNames.push(paramName);
      return '([^/]+)'; // Match any non-slash characters
    });

    // Replace * with wildcard
    regexPattern = regexPattern.replace(/\*/g, '.*');

    const compiled: CompiledPattern = {
      regex: new RegExp(`^${regexPattern}$`),
      paramNames
    };

    // Cache for future use
    this.patternCache.set(pattern, compiled);

    log.info(`Compiled pattern: ${pattern}`, compiled);

    return compiled;
  }

  /**
   * Match an incoming path against a pattern
   *
   * @param path - The incoming path to match (e.g., "/frameworks/123")
   * @param pattern - The pattern to match against (e.g., "/frameworks/:id")
   * @returns Match result with extracted parameters
   */
  private matchPattern(path: string, pattern: string): MatchResult {
    const { regex, paramNames } = this.compilePattern(pattern);
    const match = path.match(regex);

    if (!match) {
      return { matched: false, params: {} };
    }

    // Extract parameters from capture groups
    const params: Record<string, string> = {};
    paramNames.forEach((name, index) => {
      params[name] = match[index + 1]; // match[0] is full match, params start at [1]
    });

    return { matched: true, params };
  }

  /**
   * Clean a path by removing query string and hash
   *
   * @example
   * "/frameworks/123?tab=overview#section" -> "/frameworks/123"
   */
  private cleanPath(fullpath: string): string {
    return fullpath.split('?')[0].split('#')[0];
  }

  /**
   * Find the interception configuration for a given path
   *
   * Matching strategy:
   * 1. Check match cache (LRU)
   * 2. Try exact match (fast path)
   * 3. Try pattern matching against all registered patterns
   * 4. Return null if no match found
   *
   * @param fullpath - The full path to match (may include query params)
   * @returns Interception match with extracted params, or null if no match
   */
  match(fullpath: string): InterceptionMatch | null {
    // 1. Check match cache
    if (this.matchCache.has(fullpath)) {
      const cached = this.matchCache.get(fullpath)!;
      log.info(`Cache hit for path: ${fullpath}`, cached);
      return cached;
    }

    // Clean path (remove query string and hash)
    const cleanPath = this.cleanPath(fullpath);

    // 2. Try exact match first (performance optimization)
    if (this.interceptions[cleanPath]) {
      const config = this.interceptions[cleanPath];
      const result: InterceptionMatch = {
        domain: config.domain,
        name: config.name,
        props: config.props || {},
        params: {}
      };

      log.info(`Exact match for path: ${cleanPath}`, result);
      this.matchCache.set(fullpath, result);
      return result;
    }

    // 3. Try pattern matching
    for (const [pattern, config] of Object.entries(this.interceptions)) {
      // Skip if pattern is exact match (already tried above)
      if (!pattern.includes(':') && !pattern.includes('*')) {
        continue;
      }

      const { matched, params } = this.matchPattern(cleanPath, pattern);

      if (matched) {
        const result: InterceptionMatch = {
          domain: config.domain,
          name: config.name,
          props: {
            ...(config.props || {}),
            ...params  // Merge extracted params into props
          },
          params
        };

        log.info(`Pattern match for path: ${cleanPath}`, {
          pattern,
          params,
          result
        });

        this.matchCache.set(fullpath, result);
        return result;
      }
    }

    // 4. No match found
    log.warn(`No interception configuration found for path: ${fullpath}`);
    this.matchCache.set(fullpath, null);
    return null;
  }

  /**
   * Clear all caches
   * Useful when interception configuration is updated
   */
  clearCache(): void {
    this.patternCache.clear();
    this.matchCache.clear();
    log.info('Path matcher caches cleared');
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { patternCacheSize: number; matchCacheSize: number } {
    return {
      patternCacheSize: this.patternCache.size,
      matchCacheSize: this.matchCache.size
    };
  }
}
